/**
 * customPageStore.ts
 * Stores user-created custom pages in localStorage AND stages them for Supabase sync.
 */

export type BlockType = "heading" | "text" | "image" | "divider" | "cta" | "two-col" | "image-detail" | "image-grid" | "section";

export interface PageBlock {
  id: string;
  type: BlockType;
  content: string;
  subContent?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  imageAlt?: string;
  desktopPosition?: string;
  mobilePosition?: string;
  linkLabel?: string;
  linkUrl?: string;
  linkExternal?: boolean;
  imageSize?: string;
  showCaption?: boolean;
  headingLevel?: string;
  textStyle?: string;
  images?: { id: string; url: string; alt: string; caption?: string; link?: string; desktopPosition?: string; mobilePosition?: string }[];
  imageCols?: number;
  leftType?: "text" | "image";
  rightType?: "text" | "image";
  leftImage?: string;
  leftImageAlt?: string;
  rightImage?: string;
  rightImageAlt?: string;
  leftHeading?: string;
  rightHeading?: string;
  leftHeadingLevel?: string;
  rightHeadingLevel?: string;
  /** For imported home-page section blocks: carries full layout identity and data */
  sectionMeta?: {
    layout: string;
    data: Record<string, unknown>;
    config?: Record<string, unknown>;
  };
  /** Inline overrides for imported section fields (bgImage, text, etc.) */
  config?: Record<string, unknown>;
}

export interface CustomPage {
  id: string;
  slug: string;
  title: string;
  visible: boolean;
  blocks: PageBlock[];
  createdAt: string;
  updatedAt: string;
}

export const STORAGE_KEY = "ma_custom_pages_v1";

/** Stage custom pages to the global staging store without writing to localStorage.
 *  Used in admin mode where changes only commit when user clicks Save.
 */
export function stageCustomPagesOnly(pages: CustomPage[]): void {
  try {
    import("@/hooks/useAdminStorage").then(({ stagingStoreSet, stagingStoreNotify }) => {
      stagingStoreSet(STORAGE_KEY, pages);
      stagingStoreNotify(STORAGE_KEY);
    }).catch(() => { /* ignore */ });
  } catch { /* ignore */ }
}

/** Commit staged custom pages to localStorage */
export function commitCustomPages(): void {
  try {
    const pages = loadCustomPages();
    import("@/hooks/useAdminStorage").then(({ stagingStoreSet, stagingStoreNotify }) => {
      stagingStoreSet(STORAGE_KEY, pages);
      stagingStoreNotify(STORAGE_KEY);
    }).catch(() => { /* ignore */ });
  } catch { /* ignore */ }
}

function stageCustomPages(pages: CustomPage[]): void {
  try {
    import("@/hooks/useAdminStorage").then(({ stagingStoreSet, stagingStoreNotify }) => {
      stagingStoreSet(STORAGE_KEY, pages);
      stagingStoreNotify(STORAGE_KEY);
    }).catch(() => { /* ignore */ });
  } catch { /* ignore */ }
}

export function loadCustomPages(): CustomPage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return [];
}

export function saveCustomPages(pages: CustomPage[]): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(pages)); } catch { /* ignore */ }
  stageCustomPages(pages);
}

export function saveCustomPage(page: CustomPage): void {
  const pages = loadCustomPages();
  const idx = pages.findIndex((p) => p.id === page.id);
  if (idx >= 0) pages[idx] = page;
  else pages.push(page);
  saveCustomPages(pages);
}

export function deleteCustomPage(id: string): void {
  const pages = loadCustomPages().filter((p) => p.id !== id);
  saveCustomPages(pages);
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}